import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
  FormGroup,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faEdit, faEye } from '@fortawesome/free-solid-svg-icons';
import CategoryManagementApi from '@src/apis/categoryManagement';
import { COLOR } from '@src/styles/color';
import { StyledDetailCategoryModal } from './modal.styles';

const DetailCategoryModal = ({ open, category, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    translations: {
      vi: { name: '', description: '' },
      en: { name: '', description: '' },
      ko: { name: '', description: '' },
      zh: { name: '', description: '' },
    },
    order: 0,
    isActive: true,
  });

  const languages = [
    { code: 'vi', label: 'Tiếng Việt' },
    { code: 'en', label: 'Tiếng Anh' },
    { code: 'ko', label: 'Tiếng Hàn' },
    { code: 'zh', label: 'Tiếng Trung' },
  ];

  useEffect(() => {
    if (open && category) {
      setFormData(category);
      setIsEditing(false);
      setErrors({});
    }
  }, [open, category]);

  const handleInputChange = (lang, field, value) => {
    setFormData((prev) => ({
      ...prev,
      translations: {
        ...prev.translations,
        [lang]: {
          ...prev.translations[lang],
          [field]: value,
        },
      },
    }));

    if (errors[lang]?.[field]) {
      const newErrors = { ...errors };
      if (newErrors[lang]) {
        delete newErrors[lang][field];
      }
      setErrors(newErrors);
    }
  };

  const handleOrderChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      order: parseInt(value, 10) || 0,
    }));
  };

  const handleStatusChange = async (checked) => {
    try {
      const response = await CategoryManagementApi.updateCategoryStatus(
        category.id,
        {
          isActive: checked,
        },
      );

      if (response?.status) {
        setFormData((prev) => ({
          ...prev,
          isActive: checked,
        }));
        enqueueSnackbar(
          t(checked ? 'categoryActivated' : 'categoryDeactivated'),
          { variant: 'success' },
        );
      }
    } catch (error) {
      enqueueSnackbar(t('updateStatusError'), { variant: 'error' });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.translations.vi?.name?.trim()) {
      newErrors.vi = { name: t('vietnameseNameRequired') };
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      const response = await CategoryManagementApi.updateCategory(
        category.id,
        formData,
      );

      if (response?.status) {
        enqueueSnackbar(t('updateCategorySuccess'), { variant: 'success' });
        setIsEditing(false);
        onSuccess();
      }
    } catch (error) {
      enqueueSnackbar(t('updateCategoryError'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (isEditing && category) {
      setFormData(category);
      setIsEditing(false);
      setErrors({});
    } else {
      onClose();
    }
  };

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
    if (!isEditing) {
      setErrors({});
    }
  };

  const renderLanguageForm = (lang) => (
    <Box key={lang.code} className="language-section">
      <Typography variant="h6" className="language-title">
        {lang.label}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography className="field-label">
            {t('categoryName')}{' '}
            {lang.code === 'vi' && <span className="required">*</span>}
          </Typography>
          {isEditing ? (
            <TextField
              size="small"
              fullWidth
              value={formData.translations[lang.code]?.name || ''}
              onChange={(e) =>
                handleInputChange(lang.code, 'name', e.target.value)
              }
              error={!!errors[lang.code]?.name}
              helperText={errors[lang.code]?.name}
              placeholder={t('enterCategoryName')}
            />
          ) : (
            <Box className="view-content">
              <Typography variant="body1" className="view-text">
                {formData.translations[lang.code]?.name || '-'}
              </Typography>
            </Box>
          )}
        </Grid>
        <Grid item xs={12}>
          <Typography className="field-label">{t('description')}</Typography>
          {isEditing ? (
            <TextField
              size="small"
              fullWidth
              multiline
              rows={3}
              value={formData.translations[lang.code]?.description || ''}
              onChange={(e) =>
                handleInputChange(lang.code, 'description', e.target.value)
              }
              placeholder={t('enterDescription')}
            />
          ) : (
            <Box className="view-content view-content-multiline">
              <Typography variant="body1" className="view-text">
                {formData.translations[lang.code]?.description || '-'}
              </Typography>
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <StyledDetailCategoryModal open={open} onClose={onClose} maxWidth="lg">
      <DialogTitle className="dialog-title">
        {t('categoryDetail')}
        <Box className="mode-toggle">
          <Button
            variant={isEditing ? 'outlined' : 'contained'}
            size="small"
            onClick={handleEditToggle}
            startIcon={
              <FontAwesomeIcon icon={isEditing ? faEye : faEdit} size="sm" />
            }
          >
            {isEditing ? t('viewMode') : t('editMode')}
          </Button>
          <IconButton onClick={onClose}>
            <FontAwesomeIcon
              icon={faXmark}
              color={COLOR.text.highlight}
              size="sm"
            />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent className="dialog-content">
        {languages.map(renderLanguageForm)}

        <Box className="form-section">
          <Typography variant="h6" className="section-title">
            {t('settings')}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography className="field-label">{t('order')}</Typography>
              {isEditing ? (
                <TextField
                  size="small"
                  fullWidth
                  type="number"
                  value={formData.order}
                  onChange={(e) => handleOrderChange(e.target.value)}
                  placeholder={t('enterOrder')}
                />
              ) : (
                <Box className="view-content">
                  <Typography variant="body1" className="view-text">
                    {formData.order || 0}
                  </Typography>
                </Box>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) => handleStatusChange(e.target.checked)}
                      color="primary"
                      disabled={!isEditing}
                    />
                  }
                  label={t('isActive')}
                />
              </FormGroup>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCancel} variant="text">
          {isEditing ? t('cancel') : t('close')}
        </Button>
        {isEditing && (
          <Button onClick={handleSubmit} variant="contained" disabled={loading}>
            {loading ? t('updating') : t('update')}
          </Button>
        )}
      </DialogActions>
    </StyledDetailCategoryModal>
  );
};

export default DetailCategoryModal;
